import { appConfig } from '../config/appConfig';
import { apiClient } from './apiClient';
import { resolveApiErrorMessage } from '../utils/authFormUtils';

export type DeveloperApiKeyRecord = {
  id: string;
  userId: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  status: string;
  lastUsedAt: string | null;
  createdAt: string;
  revokedAt: string | null;
};

type GatewayEnvelope<T> = {
  success?: boolean;
  data?: T;
  error?: string;
  message?: string;
};

async function gatewayRequestJson<T>(path: string, options: {
  method?: 'GET' | 'POST' | 'DELETE';
  body?: unknown;
} = {}): Promise<T> {
  const accessToken = await apiClient.getAccessToken();
  if (!accessToken) {
    throw new Error('请先登录后再管理 API 密钥');
  }

  const headers: Record<string, string> = {
    Accept: 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  let body: string | undefined;
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  const url = `${appConfig.openapiGatewayBaseUrl}${path}`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: options.method || 'GET',
      headers,
      body,
    });
  } catch {
    throw new Error(
      `无法连接 OpenAPI Gateway（${appConfig.openapiGatewayBaseUrl}）。请确认已配置 VITE_OPENAPI_GATEWAY_BASE_URL（自定义域名，勿使用 fcapp.run），且 Gateway 已部署。`,
    );
  }

  const text = await response.text();
  let payload: GatewayEnvelope<T> | null = null;
  if (text) {
    try {
      payload = JSON.parse(text) as GatewayEnvelope<T>;
    } catch {
      throw new Error(text.slice(0, 200) || `Gateway 返回非 JSON（HTTP ${response.status}）`);
    }
  }

  if (!response.ok || !payload?.success) {
    throw new Error(
      resolveApiErrorMessage(payload, payload?.message || payload?.error || `请求失败（HTTP ${response.status}）`),
    );
  }
  return payload.data as T;
}

export const apiKeyService = {
  listKeys(): Promise<{ keys: DeveloperApiKeyRecord[] }> {
    return gatewayRequestJson('/api/v1/developer/keys');
  },

  createKey(input: { name?: string; scopes?: string[] }): Promise<{
    key: DeveloperApiKeyRecord;
    secret: string;
    warning?: string;
  }> {
    return gatewayRequestJson('/api/v1/developer/keys', {
      method: 'POST',
      body: {
        name: input.name?.trim() || 'default',
        scopes: input.scopes?.length ? input.scopes : ['organize', 'review', 'read'],
      },
    });
  },

  disableKey(keyId: string): Promise<{ key: DeveloperApiKeyRecord }> {
    return gatewayRequestJson(`/api/v1/developer/keys/${encodeURIComponent(keyId)}/disable`, {
      method: 'POST',
      body: {},
    });
  },

  enableKey(keyId: string): Promise<{ key: DeveloperApiKeyRecord }> {
    return gatewayRequestJson(`/api/v1/developer/keys/${encodeURIComponent(keyId)}/enable`, {
      method: 'POST',
      body: {},
    });
  },

  /** Permanently remove key record from list (any status). */
  deleteKeyPermanently(keyId: string): Promise<{ deleted: boolean; id: string }> {
    return gatewayRequestJson(`/api/v1/developer/keys/${encodeURIComponent(keyId)}/permanent`, {
      method: 'DELETE',
    });
  },

  rotateKey(keyId: string): Promise<{
    key: DeveloperApiKeyRecord;
    secret: string;
    warning?: string;
  }> {
    return gatewayRequestJson(`/api/v1/developer/keys/${encodeURIComponent(keyId)}/rotate`, {
      method: 'POST',
      body: {},
    });
  },
};
